import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Code2,
  FileWarning,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Undo2,
  X,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { RecordStatusBadge } from './RecordStatusBadge';
import { SmartCorrectionForm } from './SmartCorrectionForm';
import {
  buildCorrespondence,
  buildTargetRows,
  fieldLabel,
  formatValue,
  recordTitle,
} from './lotPresentation';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { MigrationRecord } from '../types';

interface RecordDetailDialogProps {
  record: MigrationRecord | null;
  /** fieldMapping effectif du lot (pilote la correspondance champ à champ). */
  fieldMapping?: Record<string, unknown> | null;
  open: boolean;
  onClose: () => void;
  /**
   * Notifié après une correction ou une relance unitaire (le record retourné
   * par le backend est à jour) — permet à la liste parente de se rafraîchir.
   */
  onRecordChanged?: (record: MigrationRecord) => void;
}

function JsonBlock({ data, label }: { data: Record<string, unknown> | null; label: string }) {
  if (!data) {
    return (
      <div className="text-sm text-gray-400 italic p-4">
        Aucune donnée {label.toLowerCase()}
      </div>
    );
  }

  return (
    <pre className="text-xs font-mono bg-gray-50 dark:bg-slate-900 rounded-lg p-4 overflow-auto max-h-96 border">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

/** Valeur mappedData → texte éditable (objets/tableaux en JSON compact). */
function toDraft(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/**
 * Texte édité → valeur mappedData, en préservant le type d'origine quand
 * c'est possible (nombre, booléen, objet/tableau JSON). Chaîne vide → null
 * (le backend supprime alors la clé).
 *
 * La valeur est renvoyée sans ses espaces de bord : un e-mail corrigé avec un
 * espace de fin (copier-coller) repartait sinon en échec « Email invalide »
 * sur une valeur d'apparence correcte.
 */
function fromDraft(text: string, original: unknown): unknown {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (typeof original === 'number' && trimmed !== '' && !Number.isNaN(Number(trimmed))) {
    return Number(trimmed);
  }
  if (typeof original === 'boolean' && (trimmed === 'true' || trimmed === 'false')) {
    return trimmed === 'true';
  }
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.parse(trimmed);
    } catch {
      /* pas du JSON valide : on garde la chaîne */
    }
  }
  return trimmed;
}

/** Statuts sur lesquels la correction manuelle et la relance sont permises. */
const EDITABLE_STATUSES = ['failed', 'review_needed', 'pending'];

/**
 * Détail d'un record de migration.
 *
 * Première lecture (onglet « Correspondance ») : tableau lisible par tous —
 * chaque donnée V1 et ce qu'elle devient côté V2, puis le résultat de la
 * migration (identifiants V2 créés, anomalies). Seconde lecture (onglet
 * « Technique ») : les JSON bruts source / mappé / cible.
 *
 * Sur un record non migré (échec, revue, en attente), le dialog devient une
 * interface de CORRECTION : édition des données mappées (valeur manquante ou
 * erronée, ajout d'une clé) puis relance unitaire synchrone du record.
 */
export function RecordDetailDialog({
  record,
  fieldMapping,
  open,
  onClose,
  onRecordChanged,
}: RecordDetailDialogProps) {
  const { hasPermission } = useAuth();
  // Dernier état serveur (après PATCH / relance) — prime sur la prop tant
  // qu'on regarde le même record.
  const [localRecord, setLocalRecord] = useState<MigrationRecord | null>(null);
  const [editing, setEditing] = useState(false);
  // Éditeur clé/valeur brut (filet de sécurité) — sinon le formulaire convivial.
  const [advancedMode, setAdvancedMode] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [busy, setBusy] = useState<'save' | 'saveRetry' | 'retry' | 'exclude' | 'include' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Mode « exclure » : saisie de la raison (obligatoire, reprise au rapport).
  const [excludingMode, setExcludingMode] = useState(false);
  const [excludeReason, setExcludeReason] = useState('');

  useEffect(() => {
    setLocalRecord(null);
    setEditing(false);
    setAdvancedMode(false);
    setDraft({});
    setNewKey('');
    setNewValue('');
    setActionError(null);
    setBusy(null);
    setExcludingMode(false);
    setExcludeReason('');
  }, [record?.id]);

  if (!record) return null;

  const rec = localRecord && localRecord.id === record.id ? localRecord : record;
  const mapped = rec.mappedData ?? {};
  const canExecute = hasPermission('action.migration_v1_v2.execute');
  const canEdit = canExecute && EDITABLE_STATUSES.includes(rec.status);
  const isExcluded = rec.status === 'excluded';

  const correspondence = buildCorrespondence(rec, fieldMapping);
  const targetRows = buildTargetRows(rec.targetData);
  const targetErrors = targetRows.filter((r) => r.isError);

  const startEditing = () => {
    const next: Record<string, string> = {};
    for (const [key, value] of Object.entries(mapped)) {
      next[key] = toDraft(value);
    }
    setDraft(next);
    setNewKey('');
    setNewValue('');
    setActionError(null);
    setAdvancedMode(false);
    setEditing(true);
  };

  /** Clés modifiées uniquement (merge superficiel côté backend). */
  const buildPatch = (): Record<string, unknown> => {
    const patch: Record<string, unknown> = {};
    for (const [key, text] of Object.entries(draft)) {
      if (text !== toDraft(mapped[key])) {
        patch[key] = fromDraft(text, mapped[key]);
      }
    }
    const addedKey = newKey.trim();
    if (addedKey !== '' && newValue.trim() !== '') {
      patch[addedKey] = fromDraft(newValue, undefined);
    }
    return patch;
  };

  const applyResult = (updated: MigrationRecord) => {
    setLocalRecord(updated);
    onRecordChanged?.(updated);
  };

  const handleSave = async (patch: Record<string, unknown>, thenRetry: boolean) => {
    setBusy(thenRetry ? 'saveRetry' : 'save');
    setActionError(null);
    try {
      let updated = rec;
      if (Object.keys(patch).length > 0) {
        updated = await migrationV1V2Api.updateRecordMappedData(rec.id, patch);
      }
      if (thenRetry) {
        updated = await migrationV1V2Api.retryRecord(rec.id);
      }
      applyResult(updated);
      setEditing(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setActionError(err.response?.data?.error ?? err.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleRetry = async () => {
    setBusy('retry');
    setActionError(null);
    try {
      applyResult(await migrationV1V2Api.retryRecord(rec.id));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setActionError(err.response?.data?.error ?? err.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleExclude = async () => {
    if (excludeReason.trim() === '') return;
    setBusy('exclude');
    setActionError(null);
    try {
      applyResult(await migrationV1V2Api.excludeRecord(rec.id, excludeReason.trim()));
      setExcludingMode(false);
      setExcludeReason('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setActionError(err.response?.data?.error ?? err.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleInclude = async () => {
    setBusy('include');
    setActionError(null);
    try {
      applyResult(await migrationV1V2Api.includeRecord(rec.id));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setActionError(err.response?.data?.error ?? err.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="text-base font-semibold">{recordTitle(rec)}</span>
            <span className="font-mono text-xs bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded text-gray-500">
              V1 #{rec.sourceId}
            </span>
            <RecordStatusBadge status={rec.status} />
          </DialogTitle>
        </DialogHeader>

        {rec.errorMessage && isExcluded && (
          <div className="rounded-lg bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 p-3 text-sm text-gray-700 dark:text-gray-300">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <Ban className="h-4 w-4 text-gray-500" />
              Exclu de la migration
            </div>
            {rec.errorMessage}
          </div>
        )}
        {rec.errorMessage && !isExcluded && rec.status === 'migrated' && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Migré avec avertissement
            </div>
            {rec.errorMessage}
          </div>
        )}
        {rec.errorMessage && !isExcluded && rec.status !== 'migrated' && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
            {rec.errorCode && (
              <span className="font-mono font-semibold mr-2">[{rec.errorCode}]</span>
            )}
            {rec.errorMessage}
          </div>
        )}

        {actionError && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
            {actionError}
          </div>
        )}

        {/* ------- Correction manuelle + relance unitaire + exclusion ------- */}
        {canEdit && !editing && !excludingMode && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={startEditing} disabled={busy !== null}>
              <Pencil className="h-3.5 w-3.5" />
              Corriger les données
            </Button>
            <Button size="sm" variant="outline" onClick={handleRetry} disabled={busy !== null}>
              {busy === 'retry' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              Relancer ce record
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setExcludeReason('');
                setActionError(null);
                setExcludingMode(true);
              }}
              disabled={busy !== null}
            >
              <Ban className="h-3.5 w-3.5" />
              Exclure de la migration
            </Button>
          </div>
        )}

        {/* ------- Ré-inclusion d'un record exclu ------- */}
        {canExecute && isExcluded && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleInclude} disabled={busy !== null}>
              {busy === 'include' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Undo2 className="h-3.5 w-3.5" />
              )}
              Ré-inclure dans la migration
            </Button>
          </div>
        )}

        {/* ------- Saisie de la raison d'exclusion ------- */}
        {canEdit && excludingMode && (
          <div className="rounded-lg border border-gray-200 bg-gray-50/60 dark:border-slate-700 dark:bg-slate-800/40 p-3 space-y-3">
            <p className="text-xs text-gray-600 dark:text-gray-300">
              Cet élément ne sera pas repris en V2. Il restera visible ici avec sa raison,
              sera listé dans le rapport de migration et pourra être ré-inclus à tout moment.
              La raison est reprise telle quelle dans le rapport — rédigez-la pour un lecteur
              non technique.
            </p>
            <Textarea
              value={excludeReason}
              onChange={(e) => setExcludeReason(e.target.value)}
              placeholder="Raison de l'exclusion (obligatoire)"
              rows={3}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExcludingMode(false)}
                disabled={busy !== null}
              >
                <X className="h-3.5 w-3.5" />
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleExclude}
                disabled={busy !== null || excludeReason.trim() === ''}
              >
                {busy === 'exclude' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Ban className="h-3.5 w-3.5" />
                )}
                Exclure
              </Button>
            </div>
          </div>
        )}

        {/* ------- Correction conviviale (formulaire à widgets lisibles) ------- */}
        {canEdit && editing && !advancedMode && (
          <SmartCorrectionForm
            record={rec}
            busy={busy === 'save' || busy === 'saveRetry' ? busy : null}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
            onOpenAdvanced={() => setAdvancedMode(true)}
          />
        )}

        {/* ------- Éditeur clé/valeur brut (mode « Avancé ») ------- */}
        {canEdit && editing && advancedMode && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30 p-3 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs text-gray-600 dark:text-gray-300">
                Édition avancée : corrigez les données mappées brutes de cette ligne
                (valeur manquante ou erronée) puis relancez-la. Une valeur vidée
                supprime la clé ; les objets/listes s'éditent en JSON. ⚠️ Une
                relance du lot entier ré-extrait la V1 et écrase ces corrections —
                relancez la ligne juste après l'enregistrement.
              </p>
              <button
                type="button"
                onClick={() => setAdvancedMode(false)}
                className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline decoration-dotted"
              >
                Revenir au formulaire
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border bg-white dark:bg-slate-900">
              <table className="w-full text-sm">
                <tbody>
                  {Object.keys(draft).map((key) => (
                    <tr key={key} className="border-b last:border-b-0">
                      <td className="py-1.5 px-3 w-1/3 align-middle">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {fieldLabel(key)}
                        </span>
                        <span className="block font-mono text-[10px] text-gray-400">{key}</span>
                      </td>
                      <td className="py-1.5 px-3">
                        <Input
                          className="h-8 text-sm font-mono"
                          value={draft[key]}
                          onChange={(e) =>
                            setDraft((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                        />
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="py-1.5 px-3 w-1/3">
                      <Input
                        className="h-8 text-sm font-mono"
                        placeholder="Nouvelle clé (ex. mainTeacherId)"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                      />
                    </td>
                    <td className="py-1.5 px-3">
                      <div className="flex items-center gap-1.5">
                        <Plus className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        <Input
                          className="h-8 text-sm font-mono"
                          placeholder="Valeur"
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                disabled={busy !== null}
              >
                <X className="h-3.5 w-3.5" />
                Annuler
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSave(buildPatch(), false)}
                disabled={busy !== null}
              >
                {busy === 'save' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Enregistrer
              </Button>
              <Button size="sm" onClick={() => handleSave(buildPatch(), true)} disabled={busy !== null}>
                {busy === 'saveRetry' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" />
                )}
                Enregistrer et relancer
              </Button>
            </div>
          </div>
        )}

        <Tabs defaultValue="correspondence" className="mt-2">
          <TabsList>
            <TabsTrigger value="correspondence">
              <ArrowRight className="h-3.5 w-3.5 mr-1.5" />
              Correspondance V1 → V2
            </TabsTrigger>
            <TabsTrigger value="technical">
              <Code2 className="h-3.5 w-3.5 mr-1.5" />
              Technique
            </TabsTrigger>
          </TabsList>

          {/* ------- Première lecture : correspondance lisible ------- */}
          <TabsContent value="correspondence" className="space-y-5">
            {correspondence.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Donnée</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Valeur V1</th>
                      <th className="w-8" />
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Valeur migrée (V2)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {correspondence.map((row) => (
                      <tr key={row.field} className="border-b last:border-b-0">
                        <td className="py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                          {row.label}
                        </td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                          {formatValue(row.v1Value)}
                        </td>
                        <td className="py-2 text-center text-gray-300">
                          <ArrowRight className="h-3.5 w-3.5 inline" />
                        </td>
                        <td
                          className={cn(
                            'py-2 px-3',
                            row.v2Value === null || row.v2Value === undefined || row.v2Value === ''
                              ? 'text-gray-400'
                              : 'text-blue-700 dark:text-blue-300 font-medium'
                          )}
                        >
                          {formatValue(row.v2Value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                Pas de correspondance champ à champ pour ce lot.
              </p>
            )}

            {/* Résultat V2 : identifiants créés + anomalies remontées */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Résultat de la migration
              </h4>
              {rec.targetId || targetRows.length > 0 ? (
                <div className="rounded-lg border divide-y dark:divide-slate-700">
                  {rec.targetId && (
                    <div className="flex items-start justify-between gap-4 px-3 py-2 text-sm">
                      <span className="text-gray-500">Identifiant V2 créé</span>
                      <span className="font-mono text-xs text-green-700 dark:text-green-400 break-all text-right">
                        {rec.targetId}
                      </span>
                    </div>
                  )}
                  {targetRows
                    .filter((r) => !r.isError)
                    .map((row, i) => (
                      <div key={i} className="flex items-start justify-between gap-4 px-3 py-2 text-sm">
                        <span className="text-gray-500">{row.label}</span>
                        <span
                          className={cn(
                            'text-right break-all',
                            row.isId
                              ? 'font-mono text-xs text-gray-600 dark:text-gray-400'
                              : 'text-gray-800 dark:text-gray-200'
                          )}
                        >
                          {row.value}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Pas encore de résultat V2 (enregistrement non migré).
                </p>
              )}
            </div>

            {targetErrors.length > 0 && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800 p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-semibold text-orange-800 dark:text-orange-200">
                  <FileWarning className="h-4 w-4" />
                  Anomalies signalées pendant la migration
                </div>
                {targetErrors.map((row, i) => (
                  <p key={i} className="text-xs text-orange-700 dark:text-orange-300">
                    <span className="font-medium">{row.label} :</span> {row.value}
                  </p>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ------- Seconde lecture : JSON bruts ------- */}
          <TabsContent value="technical">
            <Tabs defaultValue="source" className="mt-1">
              <TabsList>
                <TabsTrigger value="source">Source V1</TabsTrigger>
                <TabsTrigger value="mapped">Données mappées</TabsTrigger>
                <TabsTrigger value="target">Résultat V2</TabsTrigger>
              </TabsList>
              <TabsContent value="source">
                <JsonBlock data={rec.sourceData} label="source" />
              </TabsContent>
              <TabsContent value="mapped">
                <JsonBlock data={rec.mappedData} label="mappée" />
              </TabsContent>
              <TabsContent value="target">
                <JsonBlock data={rec.targetData} label="cible" />
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>

        {rec.validationErrors && rec.validationErrors.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Erreurs de validation
            </h4>
            <div className="space-y-1">
              {rec.validationErrors.map((err, i) => (
                <div key={i} className="text-xs bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded px-3 py-2">
                  {JSON.stringify(err)}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
